/**
 * Moodboard freeform helpers (mirrors BACK layout logic for UI).
 * Coords: normalized 0–1, origin top-left.
 */

export const SLOT_KEYS = [
  "hero",
  "midLeft",
  "midRight",
  "botTL",
  "botTR",
  "botBL",
  "botBR",
];

export const SLOTS_PER_PAGE = 7;
export const MIN_SLOT = 0.05;
export const LEFT_COL_RATIO = 0.26;

let slotIdSeq = 0;
export function newSlotId(prefix = "s") {
  slotIdSeq += 1;
  return `${prefix}_${Date.now().toString(36)}_${slotIdSeq}`;
}

function clamp01(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

export function clampSlot(raw = {}) {
  let w = Math.max(MIN_SLOT, clamp01(raw.w ?? 0.2));
  let h = Math.max(MIN_SLOT, clamp01(raw.h ?? 0.2));
  let x = clamp01(raw.x ?? 0);
  let y = clamp01(raw.y ?? 0);
  if (x + w > 1) x = Math.max(0, 1 - w);
  if (y + h > 1) y = Math.max(0, 1 - h);
  w = Math.min(w, 1 - x);
  h = Math.min(h, 1 - y);
  const assetRaw = raw.assetId;
  const assetId =
    assetRaw == null || assetRaw === ""
      ? null
      : Number.isFinite(Number(assetRaw))
        ? Number(assetRaw)
        : null;
  return {
    id: String(raw.id || newSlotId()),
    x,
    y,
    w: Math.max(MIN_SLOT, w),
    h: Math.max(MIN_SLOT, h),
    assetId,
  };
}

/** Aviator preset in normalized top-left coords (matches BACK). */
export function aviatorPresetSlots(assetIds = []) {
  const leftW = LEFT_COL_RATIO;
  const restW = 1 - leftW;
  const colW = restW / 3;
  const rowH = 0.5;
  const x1 = leftW;
  const x2 = leftW + colW;
  const x3 = leftW + colW * 2;
  const rects = [
    { key: "hero", x: 0, y: 0, w: leftW, h: 1 },
    { key: "midLeft", x: x1, y: 0, w: colW, h: rowH },
    { key: "midRight", x: x1, y: rowH, w: colW, h: rowH },
    { key: "botTL", x: x2, y: 0, w: colW, h: rowH },
    { key: "botTR", x: x2, y: rowH, w: colW, h: rowH },
    { key: "botBL", x: x3, y: 0, w: colW, h: rowH },
    { key: "botBR", x: x3, y: rowH, w: colW, h: rowH },
  ];
  const ids = Array.isArray(assetIds) ? assetIds : [];
  return rects.map((r, idx) => {
    const rawId = ids[idx];
    const assetId =
      rawId == null || rawId === ""
        ? null
        : Number.isFinite(Number(rawId))
          ? Number(rawId)
          : null;
    return clampSlot({
      id: `aviator_${r.key}`,
      x: r.x,
      y: r.y,
      w: r.w,
      h: r.h,
      assetId,
    });
  });
}

export function emptyFreePage() {
  return { slots: aviatorPresetSlots() };
}

function isLegacySlotsObject(slots) {
  if (!slots || typeof slots !== "object" || Array.isArray(slots)) return false;
  return SLOT_KEYS.some((k) => Object.prototype.hasOwnProperty.call(slots, k));
}

export function migrateLegacyPage(page) {
  const src = page?.slots && typeof page.slots === "object" ? page.slots : {};
  const assetIds = SLOT_KEYS.map((k) => {
    const v = src[k];
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  });
  return { slots: aviatorPresetSlots(assetIds) };
}

export function normalizeFreePage(page) {
  if (isLegacySlotsObject(page?.slots)) return migrateLegacyPage(page);
  const list = Array.isArray(page?.slots) ? page.slots : [];
  if (!list.length) return emptyFreePage();
  return { slots: list.map((s) => clampSlot(s)) };
}

export function normalizePages(raw) {
  if (!Array.isArray(raw) || !raw.length) return [emptyFreePage()];
  return raw.map((p) => normalizeFreePage(p));
}

export function applyAviatorPresetToPage(page) {
  const current = normalizeFreePage(page);
  const assetIds = current.slots.map((s) => s.assetId);
  return { slots: aviatorPresetSlots(assetIds) };
}

export function listPageAssetIds(page) {
  return normalizeFreePage(page)
    .slots.map((s) => s.assetId)
    .filter((id) => id != null)
    .map(Number);
}

export function snap(v, step = 0.02) {
  return Math.round(v / step) * step;
}

export function snapSlot(slot, step = 0.02) {
  return clampSlot({
    ...slot,
    x: snap(slot.x, step),
    y: snap(slot.y, step),
    w: snap(slot.w, step),
    h: snap(slot.h, step),
  });
}

export function splitCategoryLabel(raw) {
  const cat = String(raw || "Moodboard").trim();
  const dash = cat.search(/\s[-–—]\s/);
  if (dash < 0) return { main: cat, sub: "" };
  return {
    main: cat.slice(0, dash).trim(),
    sub: cat
      .slice(dash)
      .replace(/^\s*[-–—]\s*/, "")
      .trim(),
  };
}

export function splitStudioLine(raw) {
  const full = String(
    raw ||
      "Stage Design Studio · Irene Guijt | irene@stagedesign.es | +34 667 765 007",
  ).trim();
  const sep = full.search(/\s*[·|]\s*/);
  if (sep < 0) {
    return { studioName: full || "Stage Design Studio", contactLine: "" };
  }
  return {
    studioName: full.slice(0, sep).trim() || "Stage Design Studio",
    contactLine: full
      .slice(sep)
      .replace(/^\s*[·|]\s*/, "")
      .trim(),
  };
}
